<script setup>
import { isEmpty, isEqual, isNil } from 'lodash';
import AirDatepicker from 'air-datepicker';
import localeEs from 'air-datepicker/locale/es';
import {
  computed, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { isBlank } from '@/libs/strings';
import {
  datepickerToDate, isValidDate,
  onlyISODate, localISODateTimeString,
} from '@/libs/dates';

const props = defineProps({
  // Hay problemas con los timezones cuando usamos objetos date así que hemos decidido que
  // vamos a utilizar un String con el formato ISO y solo la fecha: 2023-09-14
  // En el caso de ser un rango, será un objeto de string tipo:
  // { start: '2023-09-14', end: '2023-09-17'}
  // En caso de timepicker == true será 2023-09-17 05:35
  modelValue: { type: [String, Object], default: null },
  icon: { type: Boolean, default: true },
  readonly: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  fixedWidth: { type: Boolean, default: true },
  autoClose: { type: Boolean, default: null },
  keyboardNav: { type: Boolean, default: false },
  position: { type: String, default: 'bottom left' },
  availableDates: { type: Array, required: false, default: null },
  // Mapa de fechas con las clases a asignarles ({ '2023-09-14': 'clase-css', ...})
  styledDates: { type: Object, required: false, default: () => ({}) },
  // True para mostrar el calendario fijo en pantalla
  inline: { type: Boolean, default: false },
  range: { type: Boolean, default: false },
  timepicker: { type: Boolean, default: false },
  onlyTimepicker: { type: Boolean, default: false },
  minutesStep: { type: Number, default: 5 },
  clearButton: { type: Boolean, default: false },
  unselectable: { type: Boolean, default: true },
  label: { type: String, default: null },
  name: { type: String, default: null },
  required: { type: Boolean, default: false },
  error: { type: [String, Array], default: null },
  formGroup: { type: Boolean, default: true },
});

const emits = defineEmits(['update:modelValue']);

let datepicker;
const input = ref();
const valorInterno = ref(props.modelValue);
const tieneTimepicker = computed(() => props.timepicker || props.onlyTimepicker);
const clasesGroup = computed(() => (
  {
    // Si no tiene icono no estiramos el input
    'datepicker-fixed': props.fixedWidth && props.icon,
    'datepicker-inline': props.inline,
    'with-timepicker': tieneTimepicker.value,
    'only-timepicker': props.onlyTimepicker,
    disabled: props.disabled,
  }
));
const labelClass = computed(() => (
  {
    'control-label': true,
    'text-center': props.inline,
    block: props.inline,
  }
));
const errorInterno = computed(() => props.error);

function hayError() {
  return !isNil(errorInterno.value);
}

function errorNormalizado() {
  if (!Array.isArray(errorInterno.value)) {
    return errorInterno.value;
  }
  return errorInterno.value.join('<br/>');
}

// Está llegando 2023-12-02T23:00:00.000Z, tenemos que coger solo
// la fecha 2023-12-03 que es la que tenemos en la lista de
// fechas porque están sin timezone
function dateAirToDateCifra(date) {
  return onlyISODate(localISODateTimeString(date));
}

function renderFechaDatepicker({ date }) {
  if (!props.availableDates && !props.styledDates) {
    return null;
  }

  const dateString = dateAirToDateCifra(date);
  const config = {};

  if (props.availableDates && props.availableDates.indexOf(dateString) < 0) {
    config.disabled = true;
  }
  if (props.styledDates[dateString]) {
    config.classes = props.styledDates[dateString];
  }
  return config;
}

function showDatepicker() {
  if (!props.disabled) {
    input.value.focus();
  }
}

function actualizarDatepickerFromDate(fecha) {
  if (isValidDate(fecha)) {
    datepicker.selectDate(fecha, { updateTime: true });
    datepicker.setViewDate(fecha);
  } else {
    input.value.value = '';
    datepicker.clear();
  }
}

function actualizarDatepicker() {
  actualizarDatepickerFromDate(datepickerToDate(input.value.value));
}

watch(() => props.modelValue, (value) => {
  // Si no para los rangos entra en bucle
  if (!props.range) {
    actualizarDatepickerFromDate(isBlank(value) ? undefined : new Date(value));
  } else {
    valorInterno.value = value;
  }
});

onMounted(() => {
  let { autoClose } = props;
  if (autoClose === null) {
    // Si tiene time visible, no autocerramos porque si no al mover las
    // barras de selección de tiempo, cierra el picker
    if (props.timepicker || props.onlyTimepicker) {
      autoClose = false;
    } else {
      // Si no tiene timepicker por defecto cerramos
      autoClose = true;
    }
  }
  const opts = {
    locale: localeEs,
    keyboardNav: props.keyboardNav,
    autoClose,
    position: props.position,
    inline: props.inline,
    // Tiene que estar a false si queremos tener un rango y seleccionar solo un día
    toggleSelected: props.unselectable,
    onlyTimepicker: props.onlyTimepicker,
    timepicker: tieneTimepicker.value,
    timeFormat: 'HH:mm',
    minutesStep: props.minutesStep,
    range: props.range,
    multipleDatesSeparator: ' - ',
    onSelect: (data) => {
      let valor;
      if (props.range) {
        valor = {
          start: dateAirToDateCifra(data.date[0]),
          end: dateAirToDateCifra(data.date[1]),
        };
      } else if (tieneTimepicker.value) {
        valor = localISODateTimeString(data.date);
      } else {
        valor = dateAirToDateCifra(data.date);
      }

      // Comprobamos los valores así porque si es un rango, son dos hash disntintos
      // pero con distintos valores de start y end
      if (!isEqual(valorInterno.value, valor)) {
        valorInterno.value = valor;
        emits('update:modelValue', valor);

        // Lanzamos el evento del html para se que puedan conectar desde fuera.
        // Si es rango no funciona, lo desconectamos porque habría que hacer una gestión especial
        if (!props.range) {
          input.value.dispatchEvent(new Event('change'));
        }
      }
    },
  };

  if (!isEmpty(valorInterno.value)) {
    if (!props.range) {
      opts.startDate = new Date(valorInterno.value);
      opts.selectedDates = [opts.startDate];
    } else {
      const fechas = [];
      if (!isEmpty(valorInterno.value.start)) {
        fechas.push(valorInterno.value.start);
      }
      if (!isEmpty(valorInterno.value.end)) {
        fechas.push(valorInterno.value.end);
      }
      if (!isEmpty(fechas)) {
        opts.selectedDates = fechas;
        opts.startDate = new Date(fechas[fechas.length - 1]);
      }
    }
  }
  opts.onRenderCell = renderFechaDatepicker;

  if (props.clearButton) {
    opts.buttons = 'clear';
  }

  datepicker = new AirDatepicker(input.value, opts);
});

onUnmounted(() => {
  datepicker.destroy();
});
</script>
<template>
  <div :class="{ 'has-error': hayError(), 'form-group': formGroup }">
    <label v-if="label" v-campo-requerido="required" :class="labelClass">
      {{ label }}
    </label>
    <div
      class="input-fecha-hora input-group"
      :class="clasesGroup"
      :disabled="disabled"
    >
      <div
        v-if="icon"
        v-show="!inline"
        class="input-group-addon"
        :disabled="disabled"
        @click="showDatepicker()"
      >
        <i v-if="onlyTimepicker" class="fa fa-fw fa-clock"></i>
        <i v-else class="fa fa-fw fa-calendar"></i>
      </div>
      <input
        v-show="!inline"
        ref="input"
        :name="name"
        class="form-control"
        autocomplete="off"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        @change="actualizarDatepicker()"
      />
    </div>
    <span
      v-if="hayError()"
      class="help-block text-danger"
    >
      {{ errorNormalizado() }}
    </span>
  </div>
</template>

<style lang="scss">
  @import "@/../assets/stylesheets/cifra/vars/main.scss";

  // Ojo que este scss no tiene que ser scoped
  .air-datepicker {
    // 1301 es el modal
    z-index: 1401 !important;

    .air-datepicker-cell.-selected- {
      background-color: $cifra-color !important;
      color: white;
    }
  }
</style>

<style lang="scss" scoped>
  @import "@/../assets/stylesheets/cifra/vars/main.scss";

  .datepicker-inline {
    margin: auto;
  }
  .datepicker-fixed.with-timepicker {
    width: 185px;
    &.only-timepicker {
      width: 105px;
    }
  }

  .input-fecha-hora:not(:disabled):not(.disabled) {
    cursor: pointer;

    input {
      cursor: pointer;
    }
  }

  .input-fecha-hora.disabled {
    cursor: not-allowed;
    .input-group-addon, .form-control {
      background-color: $color-input-disabled;
    }
  }

  input:read-only {
    background-color: white;
  }

  abbr {
    border: 0;
    text-decoration: none;
    color: $cifra-color;
  }
</style>
